import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { SupportedMedicalData } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { AddTable } from "../../../components/ui-kit/tables/add-table";
import { CellCombobox } from "../../../components/ui-kit/tables/cell-fields/cell-combobox";
import { CellDateInput } from "../../../components/ui-kit/tables/cell-fields/cell-date-input";
import { CellSelect } from "../../../components/ui-kit/tables/cell-fields/cell-select";
import { CellTextInput } from "../../../components/ui-kit/tables/cell-fields/cell-text-input";
import { CellRemoveButton } from "../../../components/ui-kit/tables/cells/cell-remove-button";
import {
  getNamesValues,
  getPrimaryUnitByName,
  getUnitsValuesByName,
  getVariableByName,
} from "../../../utils/data-utils";
import { CellGenericValueField } from "../common/cell-generic-value-field";
import { AddLabs } from "../labs/add-labs";

export const AddMedicalTable = observer(function AddMedicalTable(props: {
  app: AppModel;
}) {
  const patient = props.app.requirePatient();
  const model = useMemo(() => {
    const m = new AddLabs(patient.api, patient);
    void m.initEmpty();
    return m;
  }, [patient]);

  return (
    <AddTable
      model={model}
      setAllDates="datetime"
      columns={[
        {
          header: <Trans>Measured at</Trans>,
          mandatory: true,
          cell: (item) => (
            <CellDateInput
              model={item}
              attr="measurement_datetime"
              type="datetime-local"
            />
          ),
        },
        {
          header: <Trans>Type</Trans>,
          mandatory: true,
          cell: (item) => (
            <CellCombobox
              model={item}
              attr="measurement_type"
              values={getNamesValues(model.supportedVariables)}
              onChange={(newValue) => {
                item.measurement_unit = getPrimaryUnitByName(
                  model.supportedVariables,
                  newValue,
                );
              }}
            />
          ),
        },
        {
          header: <Trans>Value</Trans>,
          mandatory: true,
          cell: (item) => (
            <ValueCell
              item={item}
              supportedVariables={model.supportedVariables}
            />
          ),
        },
        {
          header: <Trans>Unit</Trans>,
          cell: (item) => (
            <UnitCell
              item={item}
              supportedVariables={model.supportedVariables}
            />
          ),
        },
        {
          header: <Trans>Comment</Trans>,
          cell: (item) => (
            <CellTextInput model={item} attr="comment" multiline="autosize" />
          ),
        },
        {
          header: <Trans>Remove data row</Trans>,
          cell: (item, index) => (
            <CellRemoveButton index={index} model={model} />
          ),
        },
      ]}
    />
  );
});

const ValueCell = observer(function ValueCell({
  item,
  supportedVariables,
}: {
  item: any;
  supportedVariables: SupportedMedicalData[];
}) {
  const variableDef = getVariableByName(
    supportedVariables,
    item.measurement_type,
  );

  return (
    <CellGenericValueField
      model={item}
      attr="measurement_value"
      variableDef={variableDef}
    />
  );
});

const UnitCell = observer(function UnitCell({
  item,
  supportedVariables,
}: {
  item: any;
  supportedVariables: SupportedMedicalData[];
}) {
  const values = getUnitsValuesByName(
    supportedVariables,
    item.measurement_type,
  );
  const disabled = values.length <= 1;

  return (
    <CellSelect
      model={item}
      attr="measurement_unit"
      values={values}
      disabled={disabled}
    />
  );
});
